import { c as _c } from "react/compiler-runtime";
import { MathLib } from '@morpho-org/blue-sdk';
import { toBigInt, ZERO } from '@morpho-org/dapp-core';
import { bigIntComparator } from '@morpho-org/morpho-ts';
import { useMemo } from 'react';
import { parseUnits } from 'viem';
import type { Warning } from '@/components/common/WarningIcon';
import type { PendingAction } from '@/components/VaultPage/VaultV1/VaultHeroPendingActions/pendingActions.contract';
import { getMarketRewards } from '@/helpers/rewards';
import { getShareFromTotal } from '@/helpers/vault';
import { getMarketWarningMessage } from '@/helpers/warningMessages';
import { apiWarningLevelToEnum } from '@/helpers/warnings';
import type { MarketAllocationVaultData, PendingTimelockItem } from '../config/MarketAllocation.types';
const toLltvWad = (lltv: number) => {
  return lltv > 1 ? BigInt(Math.trunc(lltv)) : parseUnits(lltv.toString(), 18);
};
const useMarketAllocationData = (vault, pendingActions) => {
  const $ = _c(8);
  let t0;
  bb0: {
    if (vault.allocations.length === 0) {
      let t1;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = {
          data: [],
          totalCount: 0
        };
        $[0] = t1;
      } else {
        t1 = $[0];
      }
      t0 = t1;
      break bb0;
    }
    let t1;
    if ($[1] !== pendingActions || $[2] !== vault.address || $[3] !== vault.allocations || $[4] !== vault.totalAssets) {
      let percentageOffset = ZERO;
      const pendingTimelocksByMarket = (pendingActions ?? []).reduce(_temp, {});
      t1 = vault.allocations.toSorted(bigIntComparator(_temp2, "desc")).map(allocation => {
        const percentShare = getShareFromTotal(vault.totalAssets, allocation.supplyAssets);
        const rewards = getMarketRewards(allocation.market, "supply");
        const aggregatedWarnings = allocation.market.warnings.reduce((warnings, warning) => {
          const message = getMarketWarningMessage(warning, allocation.market.loanAsset);
          if (!message) {
            return warnings;
          }
          warnings.push({
            level: apiWarningLevelToEnum(warning.level),
            message,
            warningType: warning.type
          });
          return warnings;
        }, []);
        const pendingTimelocksForMarket = pendingTimelocksByMarket[allocation.market.marketId.toLowerCase()] ?? [];
        const isIdle = !allocation.market.collateralAsset;
        const underlyingLiquidity = isIdle ? toBigInt(allocation.supplyAssets) ?? ZERO : toBigInt(allocation.market.state?.liquidityAssets) ?? ZERO;
        const liquidity = MathLib.min(underlyingLiquidity, toBigInt(allocation.supplyAssets) ?? ZERO);
        const allocationItem = {
          ...allocation,
          vaultAddress: vault.address,
          pendingTimelocks: pendingTimelocksForMarket,
          percentShare,
          percentageOffset,
          rewards,
          underlyingLiquidity,
          liquidity,
          market: {
            ...allocation.market,
            lltv: toLltvWad(allocation.market.lltv),
            warnings: aggregatedWarnings
          }
        };
        percentageOffset = percentageOffset + percentShare;
        percentageOffset;
        return allocationItem;
      });
      $[1] = pendingActions;
      $[2] = vault.address;
      $[3] = vault.allocations;
      $[4] = vault.totalAssets;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    const allocation_0 = t1;
    let t2;
    if ($[6] !== allocation_0) {
      t2 = {
        data: allocation_0,
        totalCount: allocation_0.length
      };
      $[6] = allocation_0;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    t0 = t2;
  }
  return t0;
};
export default useMarketAllocationData;
function _temp(acc, action) {
  if (action.type === "setCap" || action.type === "removeMarket") {
    const marketId = action.market.marketId.toLowerCase();
    if (acc[marketId] == null) {
      acc[marketId] = [];
    }
    acc[marketId].push(action);
  }
  return acc;
}
function _temp2(a) {
  return toBigInt(a.supplyAssets);
}